"use client";
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialConfig } from '@/hooks/useTrialConfig';
import { useSupportContact, waLink } from '@/hooks/useSupportContact';
import { useIsNativeApp } from '@/lib/platform';
import {
  Search, Info, CalendarCheck, Gift, Send,
  UserPlus, CalendarDays, TrendingUp, LayoutDashboard, Globe, ShieldCheck, Rocket,
  Phone, Mail, MapPin,
} from 'lucide-react';

/**
 * Public "About us / Who we are" page — two audiences in tabs:
 *  - "clients"  : why YofiPlace is great for people booking beauty services
 *  - "business" : why experts/salons should join (opens by default for owners/admins)
 * All copy via i18n (he/ru/en/fr, he primary + RTL). Company facts are fixed data.
 */
type Tab = 'clients' | 'business';

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold transition-all ${
        active ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

function FeatureCard({ icon: Icon, title, desc }: { icon: typeof Search; title: string; desc: string }) {
  return (
    <li className="flex items-start gap-3 bg-card border border-border rounded-2xl p-4">
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="space-y-1">
        <h3 className="font-bold text-sm leading-snug">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
      </div>
    </li>
  );
}

export default function AboutPage() {
  const { t } = useTranslation('common');
  const { activeRole } = useAuth();
  // Trial length comes from the admin-configured value, never a hardcoded number.
  const { trialMonths } = useTrialConfig();
  const { whatsapp } = useSupportContact();
  // K-0071. The business tab of this page carries the one remaining line that
  // names the price («N months free, then 99 ₪ a month»), and the page is
  // reachable from the app's footer. Only a confirmed website shows it.
  const isNative = useIsNativeApp();
  const [tab, setTab] = useState<Tab>('clients');

  useEffect(() => {
    // Explicit ?tab= wins; otherwise business users land on the business tab.
    const q = new URLSearchParams(window.location.search).get('tab');
    // Intentional: depends on window.location (client-only) and on the role,
    // which resolves after auth — first render must stay server-identical.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (q === 'business' || q === 'clients') { setTab(q); return; }
    if (activeRole === 'owner' || activeRole === 'admin') setTab('business');
  }, [activeRole]);

  const clientFeatures = [
    { icon: Search, k: 'cli_find' },
    { icon: Info, k: 'cli_info' },
    { icon: CalendarCheck, k: 'cli_book' },
    { icon: Gift, k: 'cli_deals' },
    { icon: Send, k: 'cli_request' },
  ];
  const bizFeatures = [
    { icon: UserPlus, k: 'biz_profile' },
    { icon: CalendarDays, k: 'biz_booking' },
    { icon: TrendingUp, k: 'biz_income' },
    { icon: LayoutDashboard, k: 'biz_crm' },
    { icon: Globe, k: 'biz_lang' },
    { icon: ShieldCheck, k: 'biz_trust' },
    { icon: Rocket, k: 'biz_start' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 space-y-10">
      <header className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-primary to-rose-400 text-white font-black text-3xl flex items-center justify-center leading-none">Y</div>
          <span className="text-3xl font-black leading-none">Yofi<span className="text-primary">Place</span></span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black">{t('about.title')}</h1>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">{t('about.subtitle')}</p>
      </header>

      <section className="bg-card border border-border rounded-2xl p-6 space-y-2">
        <h2 className="font-black text-xl">{t('about.who_title')}</h2>
        <p className="text-muted-foreground leading-relaxed">{t('about.who_text')}</p>
      </section>

      {/* Audience tabs */}
      <div className="flex gap-1.5 bg-card border border-border rounded-full p-1.5 max-w-md mx-auto">
        <TabBtn label={t('about.tab_clients')} active={tab === 'clients'} onClick={() => setTab('clients')} />
        <TabBtn label={t('about.tab_business')} active={tab === 'business'} onClick={() => setTab('business')} />
      </div>

      {tab === 'clients' ? (
        <section className="space-y-4">
          <h2 className="font-black text-xl text-center">{t('about.cli_heading')}</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {clientFeatures.map((f) => <FeatureCard key={f.k} icon={f.icon} title={t(`about.${f.k}_title`)} desc={t(`about.${f.k}_desc`)} />)}
          </ul>
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-5 text-center space-y-3">
            <p className="font-bold text-primary">{t('about.cli_cta_title')}</p>
            <Link href="/catalog" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-6 py-2.5 rounded-full hover:scale-105 transition-transform">
              <Search className="w-4 h-4" /> {t('about.cli_cta_btn')}
            </Link>
          </div>
        </section>
      ) : (
        <section className="space-y-4">
          <h2 className="font-black text-xl text-center">{t('about.biz_heading')}</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {bizFeatures.map((f) => <FeatureCard key={f.k} icon={f.icon} title={t(`about.${f.k}_title`)} desc={t(`about.${f.k}_desc`)} />)}
          </ul>
          {isNative === false && (
            <div className="bg-primary/5 border border-primary/15 rounded-2xl p-4 text-center">
              <p className="font-bold text-primary">{t('about.trial_note', { months: trialMonths })}</p>
            </div>
          )}
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-5 text-center space-y-3">
            <p className="font-bold text-primary">{t('about.biz_cta_title')}</p>
            <Link href="/business/register" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-6 py-2.5 rounded-full hover:scale-105 transition-transform">
              <Rocket className="w-4 h-4" /> {t('about.biz_cta_btn')}
            </Link>
          </div>
        </section>
      )}

      <section className="bg-card border border-border rounded-2xl p-6">
        <h2 className="font-black text-xl mb-4">{t('about.contact_title')}</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <p className="font-bold">DDreamLab Ltd · ח.פ. 517275236</p>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary shrink-0" /> זנגביל 15, נתניה, ישראל
            </span>
          </div>
          <div className="flex flex-col gap-2 text-sm sm:items-end">
            {/* K-0049: у фирмы нет телефона для звонков — связь через WhatsApp. */}
            {whatsapp && (
              <a href={waLink(whatsapp, 'שלום, הגעתי מ-YofiPlace ויש לי שאלה')}
                 target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 hover:text-primary transition-colors" dir="ltr">
                <Phone className="w-4 h-4 text-primary" /> WhatsApp
              </a>
            )}
            <a href="mailto:support@yofiplace.co.il" className="flex items-center gap-2 hover:text-primary transition-colors" dir="ltr">
              <Mail className="w-4 h-4 text-primary" /> support@yofiplace.co.il
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
